import { List, Typography, Spin, Button, message, Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styles from 'src/styles/layout.module.css';
import { getTemplates, addTemplateCompany } from 'src/features/companyTemplates/api';
import { formatDatetime } from 'src/helpers/formatters';
import { removeCompanyFromTemplateAPI } from 'src/features/templates/templates.api';
import {
  fetchCompanyOfflineTemplate,
  assignCompanyOfflineTemplate,
  clearCompanyOfflineTemplate,
} from 'src/features/templates/offlineTemplate.state';
import { useCompanyOfflineTemplate } from 'src/features/templates/templates.hooks';

interface Template {
  createdAt: string;
  updatedAt: string;
  id: string;
  name: string;
  disabled: boolean;
  meta: any;
  archived: boolean;
  publishedAt: string | null;
}

interface TemplateModalInfo {
  id: string;
  name: string;
}

const CompanyTemplatesTab: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const dispatch = useDispatch();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [relationTemplateId, setRelationTemplateId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const offlineTemplateEntity = useCompanyOfflineTemplate(companyId);
  const offlineTemplate = offlineTemplateEntity || null;
  const isOfflineTemplateLoading = offlineTemplateEntity?.isPending || false;

  const relatedTemplate = templates.find(template => template.id === relationTemplateId) || null;

  const [modalType, setModalType] = useState<
    'deleteOnline' | 'deleteOffline' | 'assignOnline' | 'assignOffline' | null
  >(null);
  const [modalTemplateInfo, setModalTemplateInfo] = useState<TemplateModalInfo | null>(null);

  // Открытие модального окна подтверждения удаления онлайн-шаблона
  const showDeleteOnlineModal = (templateId: string, templateName: string) => {
    setModalTemplateInfo({ id: templateId, name: templateName });
    setModalType('deleteOnline');
  };

  // Открытие модального окна подтверждения удаления офлайн-шаблона
  const showDeleteOfflineModal = () => {
    // Офлайн-шаблон не имеет ID в передаваемом контексте, но мы знаем его из состояния
    const templateName = offlineTemplate?.name || 'шаблон';
    setModalTemplateInfo({ id: offlineTemplate?.id || '', name: templateName }); // ID может быть null
    setModalType('deleteOffline');
  };

  // Открытие модального окна подтверждения назначения онлайн-шаблона
  const showAssignOnlineModal = (templateId: string, templateName: string) => {
    setModalTemplateInfo({ id: templateId, name: templateName });
    setModalType('assignOnline');
  };

  // Открытие модального окна подтверждения назначения офлайн-шаблона
  const showAssignOfflineModal = (templateId: string, templateName: string) => {
    setModalTemplateInfo({ id: templateId, name: templateName });
    setModalType('assignOffline');
  };

  // Закрытие модального окна
  const closeModal = () => {
    setModalType(null);
    setModalTemplateInfo(null);
  };

  // Выполнение удаления онлайн-шаблона
  const performRemoveTemplate = async () => {
    if (!companyId || !modalTemplateInfo) return;
    try {
      await removeCompanyFromTemplateAPI({ templateId: modalTemplateInfo.id, companyId });
      setRelationTemplateId(null);
      message.success(`Онлайн-шаблон "${modalTemplateInfo.name}" удален.`);
    } catch (error: any) {
      message.error('Не удалось удалить онлайн-шаблон.');
    } finally {
      closeModal();
    }
  };

  // Выполнение удаления офлайн-шаблона
  const performRemoveOfflineTemplate = async () => {
    if (!companyId) {
      closeModal();
      return;
    }
    try {
      await dispatch(clearCompanyOfflineTemplate({ companyId }));
      message.success('Офлайн-шаблон удален.');
    } catch (error: any) {
      message.error('Не удалось удалить офлайн-шаблон.');
    } finally {
      closeModal();
    }
  };

  // Выполнение добавления онлайн-шаблона
  const performAddTemplate = async () => {
    if (!companyId || !modalTemplateInfo) return;
    try {
      await addTemplateCompany({
        templateId: modalTemplateInfo.id,
        data: { companyId },
      });
      message.success(`Шаблон "${modalTemplateInfo.name}" назначен онлайн.`);

      // Обновление списка шаблонов
      setLoading(true);
      const { data } = await getTemplates({ company_id: companyId });
      if ('template' in data && Array.isArray(data.template)) {
        setTemplates(data.template);
      } else {
        setTemplates([]);
      }
      if ('relationTemplateId' in data && typeof data.relationTemplateId === 'string') {
        setRelationTemplateId(data.relationTemplateId);
      } else {
        setRelationTemplateId(null);
      }
    } catch (error: any) {
      message.error('Не удалось назначить онлайн-шаблон.');
    } finally {
      setLoading(false);
      closeModal();
    }
  };

  // Выполнение назначения офлайн-шаблона
  const performAssignOfflineTemplate = async () => {
    if (!companyId || !modalTemplateInfo) return;
    try {
      await dispatch(assignCompanyOfflineTemplate({ companyId, templateId: modalTemplateInfo.id }));
      message.success(`Офлайн-шаблон "${modalTemplateInfo.name}" назначен.`);
      dispatch(fetchCompanyOfflineTemplate({ companyId }));
    } catch (error: any) {
      message.error('Не удалось назначить офлайн-шаблон.');
    } finally {
      closeModal();
    }
  };

  // Загрузка онлайн-шаблонов и relationTemplateId
  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    getTemplates({ company_id: companyId })
      .then(({ data }) => {
        if ('template' in data && Array.isArray(data.template)) {
          setTemplates(data.template);
        } else {
          setTemplates([]);
        }
        if ('relationTemplateId' in data && typeof data.relationTemplateId === 'string') {
          setRelationTemplateId(data.relationTemplateId);
        } else {
          setRelationTemplateId(null);
        }
      })
      .catch(error => {
        console.error('Ошибка при получении шаблонов:', error);
      })
      .finally(() => setLoading(false));
  }, [companyId]);

  // Загрузка офлайн-шаблона при монтировании
  useEffect(() => {
    if (companyId) {
      dispatch(fetchCompanyOfflineTemplate({ companyId }));
    }
  }, [companyId, dispatch]);

  const isLoading = loading || isOfflineTemplateLoading;

  // Определение содержимого и заголовка модального окна
  const renderModalContent = () => {
    if (!modalTemplateInfo) return null;
    switch (modalType) {
      case 'deleteOnline':
        return `Вы уверены, что хотите удалить онлайн-шаблон "${modalTemplateInfo.name}"?`;
      case 'deleteOffline':
        return `Вы уверены, что хотите удалить офлайн-шаблон "${modalTemplateInfo.name}"?`;
      case 'assignOnline':
        return (
          <>
            {`Вы уверены, что хотите назначить шаблон "${modalTemplateInfo.name}" как онлайн-шаблон?`}
            <br />
            <br />
            {`Контент из данного шаблона будет выводиться в VR-шлемах и Личном кабинете компании.`}
          </>
        );
      case 'assignOffline':
        return (
          <>
            {`Вы уверены, что хотите назначить шаблон "${modalTemplateInfo.name}" как офлайн-шаблон?`}
            <br />
            <br />
            {`Контент из данного шаблона будет отображаться в Личном кабинете компании и не будет выводиться в VR-шлемах.`}
          </>
        );
      default:
        return null;
    }
  };

  const renderModalTitle = () => {
    switch (modalType) {
      case 'deleteOnline':
        return 'Подтвердите удаление онлайн-шаблона';
      case 'deleteOffline':
        return 'Подтвердите удаление офлайн-шаблона';
      case 'assignOnline':
        return 'Подтвердите назначение онлайн-шаблона';
      case 'assignOffline':
        return 'Подтвердите назначение офлайн-шаблона';
      default:
        return 'Подтверждение действия';
    }
  };

  // Вызов соответствующей функции действия при подтверждении
  const handleModalOk = () => {
    switch (modalType) {
      case 'deleteOnline':
        performRemoveTemplate();
        break;
      case 'deleteOffline':
        performRemoveOfflineTemplate();
        break;
      case 'assignOnline':
        performAddTemplate();
        break;
      case 'assignOffline':
        performAssignOfflineTemplate();
        break;
      default:
        closeModal();
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {isLoading ? (
          <Spin tip='Загрузка шаблонов...' />
        ) : (
          <>
            {/* Онлайн-шаблон */}
            <hr style={{ margin: '16px 0 0 0', border: 'none', borderTop: '1px solid #e8e8e8' }} />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 18,
                marginTop: 24,
              }}
            >
              <span>
                🌐 Онлайн-шаблон: <strong>{relatedTemplate ? relatedTemplate.name : 'Не найден'}</strong>
              </span>
              <Button
                type='dashed'
                style={{
                  color: relatedTemplate ? '#eb4c4c' : undefined,
                  borderColor: relatedTemplate ? '#eb4c4c' : undefined,
                }}
                onClick={() => {
                  if (relatedTemplate) {
                    showDeleteOnlineModal(relatedTemplate.id, relatedTemplate.name);
                  }
                }}
                disabled={!relatedTemplate}
              >
                Удалить
              </Button>
            </div>
            <hr style={{ margin: '17px 0 0 0', border: 'none', borderTop: '1px solid #e8e8e8' }} />
            {/* Офлайн-шаблон */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 18,
                marginTop: 24,
              }}
            >
              <span>
                📁 Офлайн-шаблон: <strong>{offlineTemplate ? offlineTemplate.name : 'Не найден'}</strong>
              </span>
              <Button
                type='dashed'
                style={{
                  color: offlineTemplate ? '#eb4c4c' : undefined,
                  borderColor: offlineTemplate ? '#eb4c4c' : undefined,
                }}
                onClick={showDeleteOfflineModal}
                disabled={!offlineTemplate}
              >
                Удалить
              </Button>
            </div>
            <hr style={{ margin: '17px 0 0 0', border: 'none', borderTop: '1px solid #e8e8e8' }} />
            {/* Список всех шаблонов */}
            {templates.length > 0 ? (
              <List
                header={'⬇️ Доступные шаблоны ⬇️'}
                style={{ fontSize: 18 }}
                dataSource={templates}
                renderItem={template => {
                  const isOnlineAssigned = template.id === relatedTemplate?.id;
                  const isOfflineAssigned = template.id === offlineTemplate?.id;

                  return (
                    <List.Item
                      key={template.id}
                      actions={[
                        // Кнопка назначения онлайн-шаблона
                        <Button
                          key={`online-${template.id}`}
                          type='dashed'
                          onClick={() => showAssignOnlineModal(template.id, template.name)}
                          disabled={isOnlineAssigned} // Отключаем, если уже назначен
                        >
                          🌐 Назначить онлайн-шаблоном
                        </Button>,
                        // Кнопка назначения офлайн-шаблона
                        <Button
                          key={`offline-${template.id}`}
                          type='dashed'
                          onClick={() => showAssignOfflineModal(template.id, template.name)}
                          disabled={isOfflineAssigned}
                        >
                          📁 Назначить офлайн-шаблоном
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        title={template.name}
                        description={
                          <>
                            <Typography.Text style={{ fontSize: 12 }}>Создан:</Typography.Text>{' '}
                            {formatDatetime(template.createdAt)}
                            <br />
                            <Typography.Text style={{ fontSize: 12 }}>Обновлён:</Typography.Text>{' '}
                            {formatDatetime(template.updatedAt)}
                            <br />
                            <Typography.Text style={{ fontSize: 12 }}>Опубликован:</Typography.Text>{' '}
                            {template.publishedAt || 'Не опубликован'}
                            <br />
                            <Typography.Text style={{ fontSize: 12 }}>Статус:</Typography.Text>{' '}
                            {template.disabled ? 'Отключен' : 'Активен'}
                          </>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            ) : (
              <Typography.Paragraph>Шаблоны отсутствуют.</Typography.Paragraph>
            )}
          </>
        )}
      </div>

      {/* Модальное окно подтверждения */}
      <Modal
        title={renderModalTitle()}
        open={modalType !== null} // Открыт, если тип модального окна задан
        onOk={handleModalOk}
        onCancel={closeModal}
        okText='Да'
        cancelText='Нет'
      >
        <p>{renderModalContent()}</p>
      </Modal>
    </div>
  );
};

export default CompanyTemplatesTab;
